import { useEffect, useState } from "react";
import {
  PayPalScriptProvider, PayPalButtons, PayPalCardFieldsProvider, PayPalNameField, PayPalNumberField,
  PayPalExpiryField, PayPalCVVField, usePayPalCardFields, usePayPalScriptReducer,
} from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { Loader2, Lock, CreditCard } from "lucide-react";
import { endpoints, apiError } from "@/lib/api";

const FIELD_STYLE = {
  input: { "font-size": "15px", "font-family": "Helvetica, Arial, sans-serif", color: "#0f172a", padding: "12px" },
  ".invalid": { color: "#dc2626" },
};

const makeHandlers = (plan, cur, onSuccess, setBusy, createOrderFn) => ({
  create: (source) => async () => {
    try {
      if (createOrderFn) return await createOrderFn(source);
      const { data } = await endpoints.paypalCreate(plan.id, cur.toLowerCase(), source);
      return data.orderId;
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail) || "Could not start the payment.");
      setBusy(false);
      throw e;
    }
  },
  approve: async (data) => {
    try {
      const { data: res } = await endpoints.paypalCapture(data.orderID);
      if (res.status === "paid") onSuccess();
      else toast.error("Payment not completed. Please try again.");
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail) || "Payment could not be verified.");
    } finally { setBusy(false); }
  },
});

const PayButton = ({ label, busy, setBusy }) => {
  const { cardFieldsForm } = usePayPalCardFields();
  const submit = async () => {
    if (!cardFieldsForm) return;
    const state = await cardFieldsForm.getState().catch(() => null);
    if (state && !state.isFormValid) { toast.error("Please check your card details."); return; }
    setBusy(true);
    cardFieldsForm.submit().catch(() => { setBusy(false); toast.error("Your card could not be charged. Please check the details or try another card."); });
  };
  return (
    <button type="button" onClick={submit} disabled={busy} data-testid="paypal-card-pay-btn"
      className="mt-3 w-full h-12 rounded-lg bg-[#0f172a] hover:bg-black text-white font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
      {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Processing…</> : <><Lock className="w-4 h-4" /> Pay {label}</>}
    </button>
  );
};

const CardForm = ({ h, label }) => {
  const [busy, setBusy] = useState(false);
  return (
    <PayPalCardFieldsProvider createOrder={h(setBusy).create("card")} onApprove={h(setBusy).approve}
      onError={() => { setBusy(false); toast.error("Card payment failed. Please check your details or try another card."); }} style={FIELD_STYLE}>
      <div data-testid="paypal-card-fields" className="space-y-0.5">
        <div className="flex items-center justify-between px-1 pb-1">
          <span className="text-[13px] font-bold text-slate-800 flex items-center gap-1.5"><CreditCard className="w-4 h-4" /> Pay with card</span>
          <span className="flex gap-1">{["VISA", "MC", "AMEX"].map((b) => <span key={b} className="text-[9px] font-extrabold px-1.5 py-0.5 rounded border border-slate-300 text-slate-600">{b}</span>)}</span>
        </div>
        <PayPalNameField placeholder="Name on card" />
        <PayPalNumberField placeholder="Card number" />
        <div className="grid grid-cols-2 gap-1"><PayPalExpiryField placeholder="MM / YY" /><PayPalCVVField placeholder="CVC" /></div>
        <PayButton label={label} busy={busy} setBusy={setBusy} />
      </div>
    </PayPalCardFieldsProvider>
  );
};

const Inner = ({ plan, cur, onSuccess, label, createOrderFn }) => {
  const [{ isResolved, isRejected }] = usePayPalScriptReducer();
  const [eligible, setEligible] = useState(null);
  const h = (setBusy) => makeHandlers(plan, cur, onSuccess, setBusy, createOrderFn);
  const noop = () => {};
  useEffect(() => {
    if (!isResolved) return;
    try { setEligible(!!window.paypal?.CardFields?.({ createOrder: async () => "", onApprove: noop }).isEligible()); }
    catch { setEligible(false); }
  }, [isResolved]);

  if (isRejected) return <div className="text-sm text-red-600 p-2" data-testid="paypal-error">PayPal could not load. Please refresh and try again.</div>;
  if (!isResolved || eligible === null)
    return <div className="flex items-center justify-center gap-2 text-slate-500 text-sm py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading secure checkout…</div>;
  const btn = h(noop);
  return (
    <>
      {eligible ? <CardForm h={h} label={label} /> : (
        <div data-testid="paypal-card-inline">
          <PayPalButtons fundingSource="card" style={{ layout: "vertical", shape: "rect", height: 46 }}
            createOrder={btn.create()} onApprove={btn.approve} onError={() => toast.error("Card payment failed — please try again.")} forceReRender={[cur, plan.id]} />
        </div>
      )}
      <div className="flex items-center gap-2 my-3 text-[11px] text-slate-500"><span className="flex-1 h-px bg-slate-200" />or pay with your PayPal account<span className="flex-1 h-px bg-slate-200" /></div>
      <PayPalButtons fundingSource="paypal" style={{ layout: "horizontal", shape: "rect", label: "paypal", height: 44, tagline: false }}
        createOrder={btn.create()} onApprove={btn.approve} onError={() => toast.error("PayPal error — please try again.")}
        onCancel={() => toast.message("Payment cancelled.")} forceReRender={[cur, plan.id]} />
    </>
  );
};

// PayPal is the main card processor: inline card form (Card Fields) right on our page + PayPal wallet.
export const PayPalCheckout = ({ plan, currency, onSuccess, amountLabel = "", createOrderFn }) => {
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState("");
  const cur = currency === "EUR" ? "EUR" : "USD";

  useEffect(() => {
    endpoints.paypalConfig().then((r) => { if (r.data?.clientId) setCfg(r.data); else setErr("Card payments are temporarily unavailable. Please use another method."); })
      .catch(() => setErr("PayPal is unavailable right now."));
  }, []);

  if (err) return <div className="mt-4 text-sm text-red-400" data-testid="paypal-error">{err}</div>;
  if (!cfg || !cfg.clientId)
    return <div className="mt-4 flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading secure checkout…</div>;

  return (
    <div className="mt-4 bg-white rounded-xl p-3.5" data-testid="paypal-checkout">
      <PayPalScriptProvider options={{ clientId: cfg.clientId, currency: cur, intent: "capture", components: "buttons,card-fields", disableFunding: "credit,paylater" }}>
        <Inner plan={plan} cur={cur} onSuccess={onSuccess} label={amountLabel} createOrderFn={createOrderFn} />
      </PayPalScriptProvider>
      <p className="mt-2 text-[11px] text-slate-500 text-center flex items-center justify-center gap-1"><Lock className="w-3 h-3" /> No PayPal account needed · card details are encrypted and processed by PayPal.</p>
    </div>
  );
};
